/**
 * DesignTokensContext - Global design tokens provider for Page Builder
 * 
 * Provides:
 * - tokens: The current design token values (colors, typography, radius, spacing, shadows)
 * - updateTokens: Function to update token values
 * - loading: Loading state
 * - injectTokensAsCSSVars: Helper to inject tokens as CSS variables into a root element
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Default tokens (fallback if API fails)
const DEFAULT_TOKENS = {
  colors: {
    bg: '#000000',
    surface: '#18181b',
    surfaceHover: '#27272a',
    text: '#ffffff',
    textMuted: '#a1a1aa',
    textSubtle: '#71717a',
    primary: '#f97316',
    primaryHover: '#ea580c',
    primaryMuted: 'rgba(249, 115, 22, 0.2)',
    accent: '#ef4444',
    accentHover: '#dc2626',
    border: '#3f3f46',
    borderSubtle: '#27272a',
    glow: 'rgba(249, 115, 22, 0.3)',
    success: '#22c55e',
    warning: '#eab308',
    error: '#ef4444'
  },
  typography: {
    fontFamilies: {
      display: "'Inter', system-ui, sans-serif",
      heading: "'Inter', system-ui, sans-serif",
      body: "'Inter', system-ui, sans-serif",
      mono: "'JetBrains Mono', 'Fira Code', monospace"
    },
    fontSizes: {
      xs: '0.75rem', sm: '0.875rem', base: '1rem', lg: '1.125rem',
      xl: '1.25rem', '2xl': '1.5rem', '3xl': '1.875rem', '4xl': '2.25rem',
      '5xl': '3rem', '6xl': '3.75rem'
    },
    fontWeights: {
      light: '300', normal: '400', medium: '500',
      semibold: '600', bold: '700', black: '900'
    },
    lineHeights: {
      tight: '1.1', snug: '1.25', normal: '1.5', relaxed: '1.75'
    }
  },
  radius: {
    none: '0', sm: '0.25rem', md: '0.5rem', lg: '0.75rem',
    xl: '1rem', '2xl': '1.5rem', full: '9999px'
  },
  spacing: {
    '0': '0', '1': '0.25rem', '2': '0.5rem', '3': '0.75rem',
    '4': '1rem', '6': '1.5rem', '8': '2rem', '10': '2.5rem',
    '12': '3rem', '16': '4rem', '20': '5rem', '24': '6rem',
    '32': '8rem', '40': '10rem', '48': '12rem'
  },
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    glow: '0 0 20px rgba(249, 115, 22, 0.3)'
  }
};

// Context
const DesignTokensContext = createContext(null);

// Convert tokens to CSS variables
export const tokensToCSSVars = (tokens) => {
  const vars = {};
  
  // Colors
  if (tokens.colors) {
    Object.entries(tokens.colors).forEach(([key, value]) => {
      vars[`--tc-color-${key}`] = value;
    });
  }
  
  // Typography - Font Families
  if (tokens.typography?.fontFamilies) {
    Object.entries(tokens.typography.fontFamilies).forEach(([key, value]) => {
      vars[`--tc-font-${key}`] = value;
    });
  }
  
  // Typography - Font Sizes
  if (tokens.typography?.fontSizes) {
    Object.entries(tokens.typography.fontSizes).forEach(([key, value]) => {
      vars[`--tc-text-${key}`] = value;
    });
  }
  
  // Typography - Font Weights
  if (tokens.typography?.fontWeights) {
    Object.entries(tokens.typography.fontWeights).forEach(([key, value]) => {
      vars[`--tc-weight-${key}`] = value;
    });
  }
  
  // Typography - Line Heights
  if (tokens.typography?.lineHeights) {
    Object.entries(tokens.typography.lineHeights).forEach(([key, value]) => {
      vars[`--tc-leading-${key}`] = value;
    });
  }
  
  // Radius
  if (tokens.radius) {
    Object.entries(tokens.radius).forEach(([key, value]) => {
      vars[`--tc-radius-${key}`] = value;
    });
  }
  
  // Spacing
  if (tokens.spacing) {
    Object.entries(tokens.spacing).forEach(([key, value]) => {
      vars[`--tc-spacing-${key}`] = value;
    });
  }
  
  // Shadows
  if (tokens.shadows) {
    Object.entries(tokens.shadows).forEach(([key, value]) => {
      vars[`--tc-shadow-${key}`] = value;
    });
  }
  
  return vars;
};

// Inject CSS variables into a root element
export const injectTokensAsCSSVars = (tokens, rootEl) => {
  if (!rootEl) return;
  
  const vars = tokensToCSSVars(tokens);
  Object.entries(vars).forEach(([key, value]) => {
    rootEl.style.setProperty(key, value);
  });
};

// Provider component
export const DesignTokensProvider = ({ children }) => {
  const [tokens, setTokens] = useState(DEFAULT_TOKENS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch tokens from API
  const fetchTokens = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Use admin endpoint if logged in, public otherwise
      const endpoint = token 
        ? `${API_URL}/api/admin/page-builder/design-tokens`
        : `${API_URL}/api/page-builder/design-tokens`;
      
      const headers = token 
        ? { 'Authorization': `Bearer ${token}` }
        : {};
      
      const response = await fetch(endpoint, { headers });
      
      if (response.ok) {
        const data = await response.json();
        setTokens(prev => ({ ...DEFAULT_TOKENS, ...data }));
        setError(null);
      } else {
        // Use defaults on error
        console.warn('Failed to fetch design tokens, using defaults');
        setTokens(DEFAULT_TOKENS);
      }
    } catch (err) {
      console.error('Error fetching design tokens:', err);
      setTokens(DEFAULT_TOKENS);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Update tokens via API
  const updateTokens = useCallback(async (updates) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/api/admin/page-builder/design-tokens`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        throw new Error('Failed to update tokens');
      }

      const updatedTokens = await response.json();
      setTokens(prev => ({ ...prev, ...updatedTokens }));
      return { success: true, tokens: updatedTokens };
    } catch (err) {
      console.error('Error updating design tokens:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Load tokens on mount
  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  // Inject tokens as CSS variables on document root
  useEffect(() => {
    injectTokensAsCSSVars(tokens, document.documentElement);
  }, [tokens]);

  const value = {
    tokens,
    loading,
    error,
    updateTokens,
    fetchTokens,
    injectTokensAsCSSVars: (rootEl) => injectTokensAsCSSVars(tokens, rootEl),
    DEFAULT_TOKENS
  };

  return (
    <DesignTokensContext.Provider value={value}>
      {children}
    </DesignTokensContext.Provider>
  );
};

// Hook to use design tokens
export const useDesignTokens = () => {
  const context = useContext(DesignTokensContext);
  // Return default context if not inside provider (for optional usage)
  if (!context) {
    return {
      tokens: DEFAULT_TOKENS,
      loading: false,
      error: null,
      updateTokens: async () => ({ success: false, error: 'No provider' }),
      fetchTokens: async () => {},
      injectTokensAsCSSVars: () => {},
      DEFAULT_TOKENS
    };
  }
  return context;
};

export default DesignTokensContext;
